/**
 * Drawing geometry + mapping-check API client.
 * IR is sharded: small summary JSON + per-page entity shards (loaded on demand).
 *
 * AWS architecture (drawing Review — keep persistence here, not in FastAPI):
 * - API Gateway REST (`classifier-api`, REACT_APP_DRAWING_GEOMETRY_API /
 *   REACT_APP_API_BASE_CLASSIFIER) -> `drawingGeometryApi` Lambda
 *   Routes: GET /geometry/..., POST /mapping-check/{confirm|publish},
 *   POST /geometry/component/reclassify
 * - IR JSON + entity shards: S3 presigned URLs returned by geometry GET
 * - Mapping template drafts: DynamoDB `drawing-mapping-template`
 * - Extraction / ingest: separate SQS-triggered Lambdas under backend/lambda/
 *
 * New drawing Review endpoints belong in drawingGeometryApi (+ wire script), deployed
 * via backend/lambda/scripts/deploy-lambda-with-version.ps1 — not backend/main.py.
 */

import {
  loadUserMarkups,
  saveUserMarkups,
  markupStorageKey,
} from '../utils/userMarkup.js';

const API_BASE =
  process.env.REACT_APP_DRAWING_GEOMETRY_API
  || process.env.REACT_APP_API_BASE_CLASSIFIER
  || '';

const S3_API_BASE = process.env.REACT_APP_S3_API_BASE || '';

function authHeaders() {
  const idToken = typeof localStorage !== 'undefined' ? localStorage.getItem('id_token') : null;
  const headers = { 'Content-Type': 'application/json' };
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  return headers;
}

function sheetIdFromIrKey(key) {
  const m = String(key || '').match(/drawing-ir\/([^/]+)\//);
  return m ? m[1] : 'sheet';
}

function summaryUrlForSheet(sheet) {
  return sheet?.irSummaryUrl || sheet?.irUrl;
}

/**
 * @param {string} projectId
 * @param {string} requirementId
 * @param {{ includeIr?: boolean }} [opts] — includeIr loads summary only (not entity shards)
 */
export async function fetchGeometrySheets(projectId, requirementId, opts = {}) {
  if (!projectId || !requirementId) return { sheets: [], unknownsRemaining: 0 };

  if (API_BASE) {
    try {
      const res = await fetch(
        `${API_BASE}/geometry/${projectId}/${requirementId}`,
        { headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `Geometry API failed (${res.status})`);

      if (opts.includeIr && Array.isArray(data.sheets) && data.sheets.length) {
        data.sheets = await Promise.all(
          data.sheets.map(async (sheet) => {
            const summaryUrl = summaryUrlForSheet(sheet);
            if (!summaryUrl) return sheet;
            try {
              const ir = await fetchDrawingIr(summaryUrl);
              return { ...sheet, ir };
            } catch (err) {
              console.warn('[drawingGeometryApi] summary load failed', sheet.irKey, err);
              // Mark the sheet as still extracting so the UI can show a spinner
              // rather than an error.  The summary file doesn't exist yet when the
              // drawing extraction Lambda is still running.
              return { ...sheet, irPending: true };
            }
          })
        );
      }
      return data;
    } catch (err) {
      console.warn('[drawingGeometryApi] geometry API unavailable, using S3 list fallback:', err);
      if (!S3_API_BASE) throw err;
      return listDrawingIrSheetsLegacy(projectId, requirementId, opts);
    }
  }

  return listDrawingIrSheetsLegacy(projectId, requirementId, opts);
}

/** @deprecated S3 list fallback when geometry API is unavailable */
async function listDrawingIrSheetsLegacy(projectId, requirementId, opts = {}) {
  if (!S3_API_BASE) {
    throw new Error('REACT_APP_API_BASE_CLASSIFIER or REACT_APP_S3_API_BASE required');
  }
  const res = await fetch(`${S3_API_BASE}/s3-files/${projectId}/${requirementId}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `Failed to list files (${res.status})`);

  const listed = (data.files || [])
    .filter((f) => f.key?.includes('/drawing-ir/') && f.key.endsWith('.json'))
    .filter((f) => !f.key.includes('-entities-p'))
    .map((f) => ({
      irKey: f.key,
      sheetId: sheetIdFromIrKey(f.key),
      irSummaryUrl: f.key.endsWith('-summary.json') ? f.url : undefined,
      irUrl: f.url,
    }));

  let unknownsRemaining = 0;
  const sheets = await Promise.all(
    listed.map(async (meta) => {
      if (!opts.includeIr) return meta;
      const url = summaryUrlForSheet(meta);
      if (!url) return meta;
      const ir = await fetchDrawingIr(url);
      unknownsRemaining += (ir.mappingCheck || {}).unknownsRemaining ?? 0;
      return { ...meta, ir };
    })
  );

  if (!opts.includeIr) {
    unknownsRemaining = sheets.reduce((n, s) => n + (s.unknownsRemaining || 0), 0);
  }

  return { sheets, unknownsRemaining };
}

function normalizePdfSourceKey(fileKey) {
  const key = String(fileKey || '').trim();
  if (!key) return null;
  const lower = key.toLowerCase();
  if (lower.endsWith('.pdf')) {
    if (lower.includes('/original/') || lower.includes('/links/')) return key;
    return null;
  }
  if (lower.endsWith('.txt') && lower.includes('/processed/')) {
    return key.replace('/processed/', '/original/').replace(/\.txt$/i, '.pdf');
  }
  return null;
}

/**
 * Build Review sheet entries from original PDF keys only — no drawing IR / vector pipeline.
 * Used when geometry API returns no sheets (extraction disabled or not run yet).
 */
export function sheetsFromPdfKeys(fileKeys) {
  const seen = new Set();
  const sheets = [];
  for (const rawKey of fileKeys || []) {
    const fileKey = normalizePdfSourceKey(rawKey);
    if (!fileKey || seen.has(fileKey)) continue;
    seen.add(fileKey);
    const baseName = fileKey.split('/').pop().replace(/\.pdf$/i, '');
    const sheetId = baseName.replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 96) || 'sheet';
    sheets.push({
      irKey: `pdf-only:${fileKey}`,
      sheetId,
      sourcePdfKey: fileKey,
      extractionStatus: 'pdf_only',
      pdfOnly: true,
      componentCount: 0,
      unknownsRemaining: 0,
    });
  }
  return sheets.sort((a, b) => a.sheetId.localeCompare(b.sheetId));
}

/**
 * List drawing PDFs for a linked folder without loading IR (safe when vector pipeline is off).
 * @param {string} projectId
 * @param {string} requirementId
 * @param {object[]} [drawingFiles] — from fetchReviewFolders `drawingFiles`
 */
export async function fetchPdfOnlyDrawingSheets(projectId, requirementId, drawingFiles = []) {
  const keys = (drawingFiles || [])
    .map((f) => f.fileKey || f.originalKey || f.key)
    .filter(Boolean);

  if (!keys.length && S3_API_BASE && projectId && requirementId) {
    try {
      const res = await fetch(`${S3_API_BASE}/s3-files/${projectId}/${requirementId}`);
      const data = await res.json();
      if (res.ok && Array.isArray(data.files)) {
        for (const f of data.files) {
          const normalized = normalizePdfSourceKey(f.key);
          if (normalized) keys.push(normalized);
        }
      }
    } catch (err) {
      console.warn('[drawingGeometryApi] pdf-only S3 list failed:', err);
    }
  }

  const sheets = sheetsFromPdfKeys(keys);
  return { sheets, unknownsRemaining: 0, pdfOnly: true };
}

/**
 * Fetch a drawing IR JSON file from a presigned S3 URL.
 *
 * Retries up to MAX_RETRIES times with exponential back-off to tolerate:
 *  - Transient network resets (ERR_CONNECTION_RESET)
 *  - Files not yet available in S3 when extraction is still in progress
 *
 * @param {string} url
 * @param {{ maxRetries?: number, initialDelayMs?: number }} [opts]
 * @returns {Promise<object>}
 */
export async function fetchDrawingIr(url, { maxRetries = 3, initialDelayMs = 1500 } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const res = await fetch(url);
      if (res.ok) return res.json();
      // 403/404 from S3 often means the file doesn't exist yet (extraction
      // still running).  Treat these as retryable on attempts < maxRetries.
      if (attempt < maxRetries && (res.status === 403 || res.status === 404)) {
        lastErr = new Error(`IR not ready (${res.status})`);
      } else {
        throw new Error(`Failed to load drawing IR (${res.status})`);
      }
    } catch (err) {
      lastErr = err;
      // Don't retry on programmer errors; only network-level failures.
      if (err?.message?.startsWith('Failed to load drawing IR (')) throw err;
    }
    if (attempt < maxRetries) {
      await new Promise((r) => setTimeout(r, initialDelayMs * 2 ** attempt));
    }
  }
  throw lastErr;
}

/**
 * Load vector entities for one page (sharded IR). Returns [] when unavailable.
 * @param {{ pageIndex: number, url?: string } | undefined} entityPage
 */
export async function fetchPageEntities(entityPage) {
  if (!entityPage?.url) return [];
  const data = await fetchDrawingIr(entityPage.url);
  return data.entities || [];
}

/**
 * @param {object} ir
 * @returns {object[]}
 */
export function unknownComponentsFromIr(ir) {
  return (ir.components || []).filter(
    (c) => c.mappingStatus === 'pending_user_confirm'
  );
}

function exemplarScore(comp) {
  let score = 0;
  if (comp?.label || comp?.value) score += 10;
  if (comp?.bbox) score += 5;
  if (comp?.kind) score += 2;
  return score;
}

function ruleLabel(ruleKey) {
  return String(ruleKey || 'unknown').replace(/_/g, ' ');
}

/**
 * One exemplar per mapping rule across all sheets (not thousands of instances).
 * @param {object[]} sheets
 */
export function buildMappingExemplarQueue(sheets) {
  const byRule = new Map();
  for (const sheet of sheets || []) {
    const ir = sheet.ir;
    if (!ir) continue;
    for (const comp of unknownComponentsFromIr(ir)) {
      const ruleKey = comp.matchedRule || comp.kind || 'unknown';
      const item = {
        ...comp,
        sheetId: sheet.sheetId,
        irKey: sheet.irKey,
        matchedRule: comp.matchedRule || ruleKey,
      };
      let bucket = byRule.get(ruleKey);
      if (!bucket) {
        byRule.set(ruleKey, {
          ruleKey,
          matchedRule: comp.matchedRule || ruleKey,
          suggestedKind: comp.kind,
          exemplar: item,
          instanceCount: 1,
          sheetIds: [sheet.sheetId],
        });
      } else {
        bucket.instanceCount += 1;
        if (!bucket.sheetIds.includes(sheet.sheetId)) {
          bucket.sheetIds.push(sheet.sheetId);
        }
        if (exemplarScore(item) > exemplarScore(bucket.exemplar)) {
          bucket.exemplar = item;
          bucket.suggestedKind = comp.kind;
        }
      }
    }
  }
  return Array.from(byRule.values()).sort((a, b) => a.ruleKey.localeCompare(b.ruleKey));
}

export { ruleLabel };

/**
 * @param {object[]} sheets — { ir, irKey, sheetId }
 * @returns {object[]}
 */
export function flattenUnknownQueue(sheets) {
  const queue = [];
  for (const sheet of sheets) {
    const ir = sheet.ir;
    if (!ir) continue;
    for (const comp of unknownComponentsFromIr(ir)) {
      queue.push({
        ...comp,
        sheetId: sheet.sheetId,
        irKey: sheet.irKey,
      });
    }
  }
  return queue;
}

/**
 * @param {object} payload
 */
export async function confirmMapping(payload) {
  if (!API_BASE) throw new Error('Drawing geometry API not configured');
  const res = await fetch(`${API_BASE}/mapping-check/confirm`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      applyAcrossRequirement: true,
      confirmByRule: true,
      ...payload,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `Confirm failed (${res.status})`);
  return data;
}

/**
 * Update a single component on one sheet (instance scope). Persists to S3 via drawingGeometryApi Lambda.
 * @param {object} payload
 */
export async function reclassifyComponent(payload) {
  if (!API_BASE) throw new Error('Drawing geometry API not configured');
  const res = await fetch(`${API_BASE}/geometry/component/reclassify`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `Reclassify failed (${res.status})`);
  return data;
}

/**
 * @param {object} payload
 */
export async function publishMappingTemplate(payload) {
  if (!API_BASE) throw new Error('Drawing geometry API not configured');
  const res = await fetch(`${API_BASE}/mapping-check/publish`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `Publish failed (${res.status})`);
  return data;
}

// ---------------------------------------------------------------------------
// Sheet frame proposals and page-band decisions (Slice 2c).
//
// Two rules govern everything below, both from the Slice 2 design review §3.2:
//
//   1. THE CLIENT NEVER COMPUTES A FINGERPRINT. `documentDigest` and
//      `authorizationFingerprint` are echoed back exactly as the server issued
//      them; the server re-derives both from its own stored proposal record and
//      rejects a mismatch with 404. There is deliberately no hashing, no
//      normalising and no defaulting of either value here — a client that could
//      construct one could authorize a band that is not a current proposal.
//
//   2. NOTHING HERE AUTHORIZES ANYTHING. These endpoints record a reviewer's
//      verdict for later review. They have no extraction effect, and the
//      responses carry `isAuthoritative: false` / `changesExtraction: false` to
//      say so structurally rather than by naming convention.
// ---------------------------------------------------------------------------

/** `POST /sheet-frame/decisions` outcomes the caller is expected to render. */
export const SHEET_FRAME_DECISION_STORED = 'stored';
export const SHEET_FRAME_DECISION_CONFLICT = 'conflict';

/**
 * A decision request that could not be stored and is not a conflict.
 *
 * Carries `status` so the panel can distinguish "this page moved on" (404) from
 * "this request was malformed" (422) without parsing message text. The message
 * is the server's short reason enum — never a payload, never an identifier.
 */
export class SheetFrameDecisionError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'SheetFrameDecisionError';
    this.status = status;
  }
}

/**
 * Proposals for one page of one sheet.
 *
 * Always resolves for any answer the API considers normal. `available: false`
 * with an `unavailableReason` — `no_sidecar` while the producer flag is off,
 * `ambiguous_document`, `page_not_analyzed`, and the rest of the fixed enum —
 * is a 200 by design, because "nothing to confirm right now" is an ordinary
 * state of this endpoint and a rejected promise would train the UI to retry
 * something that will never succeed. Only a genuine transport or 5xx failure
 * rejects.
 *
 * @param {string} projectId
 * @param {string} requirementId
 * @param {string} sheetId
 * @param {number} pageIndex zero-based
 */
export async function fetchSheetFrameProposals(projectId, requirementId, sheetId, pageIndex) {
  if (!API_BASE) throw new Error('Drawing geometry API not configured');
  if (!projectId || !requirementId || !sheetId) {
    throw new Error('projectId, requirementId and sheetId are required');
  }
  if (!Number.isInteger(pageIndex) || pageIndex < 0) {
    throw new Error('pageIndex must be a non-negative integer');
  }

  const path = [
    encodeURIComponent(projectId),
    encodeURIComponent(requirementId),
    encodeURIComponent(sheetId),
  ].join('/');
  const res = await fetch(
    `${API_BASE}/sheet-frame/proposals/${path}?pageIndex=${pageIndex}`,
    { headers: authHeaders() }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `Sheet frame proposals failed (${res.status})`);
  return data;
}

/**
 * Record or flip one page-band decision.
 *
 * FIRST DECISION vs FLIP is the caller's most consequential choice, and the two
 * are distinguished by `expectedDecisionId`, not by `expectedRevision`:
 *
 *   · First decision — `expectedRevision: 0`, `expectedDecisionId` omitted. The
 *     server writes under `attribute_not_exists(pk)`.
 *   · Flip — echo BOTH the `decisionId` and `revision` the GET returned. The
 *     server writes under `revision = :expected AND decisionId = :seen`.
 *
 * `expectedRevision` alone cannot discriminate: two reviewers who each loaded
 * an undecided page both hold revision 0, so a server condition that accepted
 * revision 0 would let the second silently replace the first. `expectedRevision`
 * is therefore required alongside `expectedDecisionId`, and supplying an id
 * without a revision is a caller bug caught here rather than by a round-trip
 * 422.
 *
 * @returns {Promise<{outcome: string, decision?: object, current?: object|null}>}
 *   `{ outcome: 'stored', decision }` on 200, or `{ outcome: 'conflict',
 *   current }` on 409 — where `current` is the stored row the caller must
 *   present and adopt. A conflict is never silently retried or overwritten.
 * @throws {SheetFrameDecisionError} for any other non-OK status.
 */
export async function saveSheetFrameDecision({
  projectId,
  requirementId,
  sheetId,
  pageIndex,
  documentDigest,
  authorizationFingerprint,
  verdict,
  expectedRevision,
  expectedDecisionId = null,
}) {
  if (!API_BASE) throw new Error('Drawing geometry API not configured');
  if (verdict !== 'valid' && verdict !== 'rejected') {
    throw new Error('verdict must be "valid" or "rejected"');
  }
  if (!Number.isInteger(expectedRevision) || expectedRevision < 0) {
    throw new Error('expectedRevision must be a non-negative integer');
  }
  if (expectedDecisionId != null && typeof expectedDecisionId !== 'string') {
    throw new Error('expectedDecisionId must be a string when present');
  }

  const body = {
    sheetId,
    pageIndex,
    // Echoed verbatim from the GET. Never derived, never recomputed.
    documentDigest,
    authorizationFingerprint,
    verdict,
    expectedRevision,
  };
  // OMITTED, not null-or-omitted-inconsistently: absent means "I saw no stored
  // decision", which is exactly what permits the strict first write.
  if (expectedDecisionId != null) body.expectedDecisionId = expectedDecisionId;

  const res = await fetch(`${API_BASE}/sheet-frame/decisions`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ projectId, requirementId, ...body }),
  });
  const data = await res.json();

  if (res.status === 409) {
    return { outcome: SHEET_FRAME_DECISION_CONFLICT, current: data?.current ?? null };
  }
  if (!res.ok) {
    throw new SheetFrameDecisionError(
      data?.error || `Sheet frame decision failed (${res.status})`,
      res.status
    );
  }
  return { outcome: SHEET_FRAME_DECISION_STORED, decision: data };
}

/**
 * @param {string} projectId
 * @param {string} requirementId
 * @returns {Promise<number>}
 */
export async function countDrawingUnknowns(projectId, requirementId) {
  try {
    const { unknownsRemaining = 0 } = await fetchGeometrySheets(projectId, requirementId);
    return unknownsRemaining;
  } catch {
    return 0;
  }
}

/**
 * Lightweight aggregate stats for a folder — used by FolderCard and ReviewLanding.
 * @param {string} projectId
 * @param {string} requirementId
 * @returns {Promise<{
 *   totalComponents: number,
 *   unknownsRemaining: number,
 *   unknownRulesRemaining: number,
 *   totalCrossRefs: number,
 *   sheetCount: number,
 *   sheetsExtracting: number,
 *   sheetsReady: number,
 *   extractionComplete: boolean,
 * }>}
 */
export async function fetchFolderDrawingStats(projectId, requirementId) {
  try {
    const data = await fetchGeometrySheets(projectId, requirementId);
    const sheets = data.sheets || [];
    const sheetsExtracting = sheets.filter((s) => s.extractionStatus === 'extracting').length;
    const sheetsReady = sheets.filter((s) => s.extractionStatus !== 'extracting' && s.componentCount > 0).length;
    return {
      totalComponents: data.totalComponents ?? sheets.reduce((n, s) => n + (s.componentCount || 0), 0),
      unknownsRemaining: data.unknownsRemaining ?? 0,
      unknownRulesRemaining: data.unknownRulesRemaining ?? 0,
      totalCrossRefs: data.totalCrossRefs ?? sheets.reduce((n, s) => n + (s.crossRefCount || 0), 0),
      sheetCount: sheets.length,
      sheetsExtracting,
      sheetsReady,
      extractionComplete: sheets.length > 0 && sheetsExtracting === 0,
    };
  } catch {
    return {
      totalComponents: 0, unknownsRemaining: 0, unknownRulesRemaining: 0,
      totalCrossRefs: 0, sheetCount: 0, sheetsExtracting: 0, sheetsReady: 0,
      extractionComplete: false,
    };
  }
}

// ── User markup persistence (API when wired; localStorage fallback for pilot) ──

function markupLocalKey(projectId, requirementId, sheetId) {
  return markupStorageKey(projectId, requirementId, sheetId);
}

/**
 * Load user-drawn markups — tries geometry API, falls back to localStorage.
 */
export async function fetchUserMarkupsApi(projectId, requirementId, sheetId) {
  const localKey = markupLocalKey(projectId, requirementId, sheetId);
  const local = loadUserMarkups(localKey);
  if (!API_BASE || !projectId || !requirementId || !sheetId) {
    return { markups: local, source: 'local' };
  }
  try {
    const res = await fetch(
      `${API_BASE}/geometry/markup/${projectId}/${requirementId}/${encodeURIComponent(sheetId)}`,
      { headers: authHeaders() },
    );
    if (res.status === 404) return { markups: local, source: 'local' };
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || `Markup fetch failed (${res.status})`);
    const markups = data.markups || data.items || [];
    if (markups.length) {
      saveUserMarkups(localKey, markups);
      return { markups, source: 'api' };
    }
    return { markups: local, source: local.length ? 'local' : 'empty' };
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.info('[drawingGeometryApi] markup API unavailable, using local:', err.message);
    }
    return { markups: local, source: 'local' };
  }
}

/**
 * Persist user markups — writes local always; syncs API when available.
 */
export async function persistUserMarkupsApi(projectId, requirementId, sheetId, markups) {
  const localKey = markupLocalKey(projectId, requirementId, sheetId);
  saveUserMarkups(localKey, markups);
  if (!API_BASE || !projectId || !requirementId || !sheetId) {
    return { ok: true, source: 'local' };
  }
  try {
    const res = await fetch(
      `${API_BASE}/geometry/markup/${projectId}/${requirementId}/${encodeURIComponent(sheetId)}`,
      {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ markups }),
      },
    );
    if (res.ok) return { ok: true, source: 'api' };
  } catch {
    // local already saved
  }
  return { ok: true, source: 'local' };
}

/**
 * Delete one markup — local + API when wired.
 */
export async function deleteUserMarkupApi(projectId, requirementId, sheetId, markupId, markupsAfterDelete) {
  return persistUserMarkupsApi(projectId, requirementId, sheetId, markupsAfterDelete);
}
